import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { CalendarTask, ParsedCalendarQuery } from "@silph-scope/shared";
import {
  eachDate,
  formatDayLabel,
  formatRangeLabel,
  isWeekend,
  parseCalendarQuery,
  serializeCalendarQuery,
  shiftStart,
  todayInLosAngeles
} from "@silph-scope/shared";
import { useCalendar, useDashboard, useMe } from "../api/hooks";
import { apiFetch } from "../api/client";
import { RosterAdminButton } from "../components/RosterEditor";

const DASHBOARD_ID = "calendar";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ASSIGNEE_COLORS: Record<string, string> = {
  "Alex Nii": "#6e8dff",
  "Andres Perez": "#3ee089",
  "Minji Kim": "#ffd268",
  "Adam Caudell": "#ff97a0",
  "Moroni Myers": "#c084fc",
  "Sarida Ngo": "#22d3ee",
  "Thuan Quach": "#fb923c",
  "Tito Tay": "#f472b6"
};

function colorFor(name: string): string {
  return ASSIGNEE_COLORS[name] ?? "#99a0ae";
}

function taskStatus(task: CalendarTask): "done" | "blocked" | "open" {
  if (task.completed) return "done";
  if (task.blocked) return "blocked";
  return "open";
}

const STATUS_ORDER = { open: 0, blocked: 1, done: 2 } as const;

function compareByStatus(a: CalendarTask, b: CalendarTask): number {
  const byStatus = STATUS_ORDER[taskStatus(a)] - STATUS_ORDER[taskStatus(b)];
  if (byStatus !== 0) return byStatus;
  return a.title.localeCompare(b.title);
}

function compareByDateThenStatus(a: CalendarTask, b: CalendarTask): number {
  const byDate = a.dueOn.localeCompare(b.dueOn);
  if (byDate !== 0) return byDate;
  return compareByStatus(a, b);
}

function statusLabel(task: CalendarTask): string {
  const status = taskStatus(task);
  return status === "done" ? "Done" : status === "blocked" ? "Blocked" : "Due";
}

function StatusPill({ task }: { task: CalendarTask }) {
  const status = taskStatus(task);
  const kind = status === "done" ? "done" : status === "blocked" ? "bad" : "ok";
  return <span className={`pill ${kind}`}>{statusLabel(task)}</span>;
}

function StatusIcon({ task }: { task: CalendarTask }) {
  const status = taskStatus(task);
  if (status === "open") return null;
  return (
    <i
      className={status === "done" ? "ri-checkbox-circle-fill" : "ri-lock-2-fill"}
      aria-label={status === "done" ? "Done" : "Blocked"}
    />
  );
}

function isOpenTask(task: CalendarTask): boolean {
  return !task.completed && !task.blocked;
}

function assigneeCounts(tasks: CalendarTask[], roster: string[]) {
  const counts = new Map<string, number>();
  for (const task of tasks) {
    if (!isOpenTask(task)) continue;
    counts.set(task.assignee, (counts.get(task.assignee) ?? 0) + 1);
  }
  const extras = [...counts.keys()].filter((name) => !roster.includes(name));
  return [...roster, ...extras]
    .filter((name) => counts.has(name))
    .map((name) => ({ name, count: counts.get(name)! }));
}

function AssigneeCounts({
  tasks,
  roster,
  remaining
}: {
  tasks: CalendarTask[];
  roster: string[];
  remaining: number;
}) {
  const rows = assigneeCounts(tasks, roster);
  if (rows.length === 0 && remaining === 0) return null;
  return (
    <span className="cal-counts">
      {rows.map((row) => (
        <span
          key={row.name}
          className="cal-count"
          style={{ background: colorFor(row.name) }}
          title={`${row.name} · ${row.count} open`}
        >
          {row.count}
        </span>
      ))}
      {remaining > 0 && (
        <span className="cal-count cal-total" title={`${remaining} open remaining`}>
          Total {remaining}
        </span>
      )}
    </span>
  );
}

export function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(() => parseCalendarQuery(Object.fromEntries(searchParams.entries())), [searchParams]);
  const urlSearch = new URLSearchParams(serializeCalendarQuery(query)).toString();
  const fetchSearch = new URLSearchParams(
    serializeCalendarQuery({ ...query, selectedAssignees: null, day: null })
  ).toString();
  const queryClient = useQueryClient();
  const { data: dashboard } = useDashboard(DASHBOARD_ID);
  const { data: me } = useMe();
  const calendar = useCalendar(fetchSearch);
  const payload = calendar.data?.data;
  const today = todayInLosAngeles();
  const [openTask, setOpenTask] = useState<CalendarTask | null>(null);

  useEffect(() => {
    if (searchParams.toString() === urlSearch) return;
    setSearchParams(serializeCalendarQuery(query), { replace: true });
  }, [query, urlSearch, searchParams, setSearchParams]);

  useEffect(() => {
    if (!openTask) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenTask(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [openTask]);

  function update(patch: Partial<ParsedCalendarQuery>) {
    const next = { ...query, ...patch };
    if (!next.includeWeekends && next.day && isWeekend(next.day)) next.day = null;
    setSearchParams(serializeCalendarQuery(next), { replace: true });
  }

  const roster = payload?.roster ?? Object.keys(ASSIGNEE_COLORS);
  const selected = query.selectedAssignees;
  const activeAssignees = selected === null ? roster : selected;
  const tasks = (payload?.tasks ?? []).filter((task) => activeAssignees.includes(task.assignee));
  const start = payload?.start ?? query.start;
  const end = payload?.end ?? start;
  const days = eachDate(start, end).filter((iso) => query.includeWeekends || !isWeekend(iso));
  const weekdayLabels = query.includeWeekends ? WEEKDAYS : WEEKDAYS.slice(0, 5);
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarTask[]>();
    for (const task of tasks) {
      const list = map.get(task.dueOn) ?? [];
      list.push(task);
      map.set(task.dueOn, list);
    }
    for (const list of map.values()) list.sort(compareByStatus);
    return map;
  }, [tasks]);

  const visibleTasks = query.includeWeekends ? tasks : tasks.filter((task) => !isWeekend(task.dueOn));
  const listTasks = (query.day ? visibleTasks.filter((task) => task.dueOn === query.day) : visibleTasks)
    .slice()
    .sort(query.day ? compareByStatus : compareByDateThenStatus);
  const interval = dashboard?.refreshInterval ?? "15m";
  const syncedAt = calendar.data?.syncedAt ? new Date(calendar.data.syncedAt) : null;
  const isAdmin = me?.roles.includes("admin") ?? false;

  async function handleRefresh() {
    await apiFetch(`/api/dashboards/${DASHBOARD_ID}/refresh`, { method: "POST" });
    await queryClient.refetchQueries({ queryKey: ["panel", "calendar"] });
  }

  function toggleAssignee(name: string) {
    const current = activeAssignees;
    const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name];
    update({
      selectedAssignees: next.length === roster.length ? null : next
    });
  }

  return (
    <>
      <header className="page-header">
        <div>
          <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            Dashboards <i className="ri-arrow-right-s-line" /> Operations
          </div>
          <h1>Calendar</h1>
          <p>Assigned work by due date, color-coded by person.</p>
        </div>
        <div className="header-controls">
          <div className="chip">
            <span className="dot" />
            {syncedAt ? `Live · synced ${relativeTime(syncedAt)}` : "Awaiting first sync"}
          </div>
          <div className="chip">
            <i className="ri-refresh-line" />
            Refresh Rate: <strong>{interval}</strong>
          </div>
          <RosterAdminButton pageId={DASHBOARD_ID} />
          {isAdmin && (
            <button className="btn-primary" type="button" onClick={handleRefresh} disabled={calendar.isFetching}>
              <i className="ri-restart-line" />
              Refresh now
            </button>
          )}
        </div>
      </header>

      <section className="cal-toolbar">
        <div className="cal-view-toggle">
          <button className={query.view === "week" ? "active" : ""} type="button" onClick={() => update({ view: "week", day: null })}>
            By Week
          </button>
          <button className={query.view === "month" ? "active" : ""} type="button" onClick={() => update({ view: "month", day: null })}>
            By Month
          </button>
        </div>
        <div className="cal-nav">
          <button className="icon-btn" type="button" title="Previous" onClick={() => update({ start: shiftStart(query.view, query.start, -1), day: null })}>
            <i className="ri-arrow-left-s-line" />
          </button>
          <strong>{formatRangeLabel(query.view, query.start, end)}</strong>
          <button className="icon-btn" type="button" title="Next" onClick={() => update({ start: shiftStart(query.view, query.start, 1), day: null })}>
            <i className="ri-arrow-right-s-line" />
          </button>
          <button className="btn-ghost" type="button" onClick={() => update({ start: today, day: null })}>
            Today
          </button>
        </div>
        <label className="cal-check">
          <input type="checkbox" checked={query.includeBlocked} onChange={(e) => update({ includeBlocked: e.target.checked })} />
          Include blocked
        </label>
        <label className="cal-check">
          <input type="checkbox" checked={query.includeDone} onChange={(e) => update({ includeDone: e.target.checked })} />
          Include done
        </label>
        <label className="cal-check">
          <input type="checkbox" checked={query.includeWeekends} onChange={(e) => update({ includeWeekends: e.target.checked })} />
          Include weekends
        </label>
      </section>

      <section className="cal-legend" aria-label="Assignees">
        <div className="cal-legend-head">
          <span className="kpi-label">Assignees</span>
          <div className="cal-assignee-actions">
            <button type="button" onClick={() => update({ selectedAssignees: null })}>
              All
            </button>
            <button type="button" onClick={() => update({ selectedAssignees: [] })}>
              None
            </button>
          </div>
        </div>
        <div className="cal-legend-pills">
          {roster.map((name) => {
            const on = activeAssignees.includes(name);
            return (
              <button
                key={name}
                type="button"
                className={`cal-legend-pill${on ? " is-on" : ""}`}
                style={on ? { background: colorFor(name) } : undefined}
                aria-pressed={on}
                onClick={() => toggleAssignee(name)}
              >
                <i className={on ? "ri-check-line" : "ri-add-line"} aria-hidden />
                {name}
              </button>
            );
          })}
        </div>
      </section>

      {calendar.isError && (
        <div className="stale-banner">
          <i className="ri-error-warning-fill" />
          Could not load calendar tasks.
        </div>
      )}

      <section className={`cal-grid cal-grid-${query.view}${query.includeWeekends ? "" : " cal-hide-weekends"}`}>
        {weekdayLabels.map((day) => (
          <div className="cal-dow" key={day}>
            {day}
          </div>
        ))}
        {days.map((iso) => {
          const dayTasks = byDay.get(iso) ?? [];
          const remaining = dayTasks.filter(isOpenTask).length;
          const inMonth = iso.slice(0, 7) === query.start.slice(0, 7) || query.view === "week";
          const selectedDay = query.day === iso;
          return (
            <div
              key={iso}
              className={`cal-cell${iso === today ? " is-today" : ""}${selectedDay ? " is-selected" : ""}${inMonth ? "" : " is-outside"}`}
              onClick={() => update({ day: selectedDay ? null : iso })}
            >
              <span className="cal-cell-date">{Number(iso.slice(8))}</span>
              <AssigneeCounts tasks={dayTasks} roster={roster} remaining={remaining} />
              {query.view === "week" && (
                <span
                  className="cal-cell-chips"
                  onWheel={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  {dayTasks.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      className={`cal-chip${task.completed ? " is-done" : ""}${task.blocked && !task.completed ? " is-blocked" : ""}`}
                      style={{ background: colorFor(task.assignee) }}
                      title={`${task.title}\n${task.assignee} · ${statusLabel(task)} · ${task.type} · ${task.size} · ${task.partner}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenTask(task);
                      }}
                    >
                      <StatusIcon task={task} />
                      {task.title}
                    </button>
                  ))}
                </span>
              )}
            </div>
          );
        })}
      </section>

      <section className="card table-panel cal-list">
        <div className="panel-header">
          <h2>{query.day ? formatDayLabel(query.day) : "Tasks in view"}</h2>
          <span>
            {listTasks.length} task{listTasks.length === 1 ? "" : "s"}
            {query.day && (
              <button className="cal-clear-day" type="button" onClick={() => update({ day: null })}>
                Show all
              </button>
            )}
          </span>
        </div>
        <div className="cal-list-header">
          <span>Status</span>
          <span>Due</span>
          <span>Task</span>
          <span>Type</span>
          <span>Size</span>
          <span>Partner</span>
          <span>Assignee</span>
          <span />
        </div>
        {calendar.isLoading && <p className="cal-empty">Loading…</p>}
        {!calendar.isLoading && listTasks.length === 0 && <p className="cal-empty">No tasks for this view.</p>}
        {listTasks.map((task) => (
          <div className="cal-list-row" key={task.id} onClick={() => setOpenTask(task)}>
            <StatusPill task={task} />
            <span className="mono">{task.dueOn.slice(5)}</span>
            <span className="task-title" title={task.project || undefined}>{task.title}</span>
            <span>{task.type}</span>
            <span>{task.size}</span>
            <span>{task.partner}</span>
            <span className="cal-assignee-cell">
              <span className="cal-swatch" style={{ background: colorFor(task.assignee) }} />
              {task.assignee}
            </span>
            <a
              className="cal-asana"
              href={task.asanaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              Asana <i className="ri-external-link-line" />
            </a>
          </div>
        ))}
      </section>
      {openTask && <TaskModal task={openTask} onClose={() => setOpenTask(null)} />}
    </>
  );
}

function TaskModal({ task, onClose }: { task: CalendarTask; onClose: () => void }) {
  const projects = task.project
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);

  return (
    <div className="cal-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="cal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cal-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="cal-modal-head">
          <div>
            <StatusPill task={task} />
            <h2 id="cal-modal-title">{task.title}</h2>
          </div>
          <button className="icon-btn" type="button" title="Close" onClick={onClose}>
            <i className="ri-close-line" />
          </button>
        </div>
        <dl className="cal-modal-meta">
          <div>
            <dt>Assignee</dt>
            <dd className="cal-assignee-cell">
              <span className="cal-swatch" style={{ background: colorFor(task.assignee) }} />
              {task.assignee || "—"}
            </dd>
          </div>
          <div>
            <dt>Due date</dt>
            <dd>{formatDayLabel(task.dueOn)}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{task.type || "—"}</dd>
          </div>
          <div>
            <dt>Size</dt>
            <dd>{task.size || "—"}</dd>
          </div>
          <div>
            <dt>Partner</dt>
            <dd>{task.partner || "—"}</dd>
          </div>
          <div className="cal-modal-wide">
            <dt>Projects</dt>
            <dd>{projects.length > 0 ? projects.join(" · ") : "—"}</dd>
          </div>
        </dl>
        <div className="cal-modal-desc">
          <div className="kpi-label">Description</div>
          {task.description ? <p>{task.description}</p> : <p className="cal-empty">No description.</p>}
        </div>
        <a className="btn-primary cal-modal-asana" href={task.asanaUrl} target="_blank" rel="noopener noreferrer">
          Open in Asana <i className="ri-external-link-line" />
        </a>
      </div>
    </div>
  );
}

function relativeTime(date: Date): string {
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
